import { createTheme, responsiveFontSizes } from "@mui/material/styles";
import type {} from "@mui/x-data-grid/themeAugmentation";

/* ===========================
   PALETTE AUGMENTATION
=========================== */
declare module "@mui/material/styles" {
  interface Palette {
    customBackground: { box: string };
    highlightedRow: { main: string };
    link: { main: string };
  }
  interface PaletteOptions {
    customBackground?: { box: string };
    highlightedRow?: { main: string };
    link?: { main: string };
  }
}

/* ===========================
   TYPES
=========================== */
export type ThemeColors = {
  primary: { main: string; light?: string; dark?: string };
  secondary: { main: string };
  background: {
    default: string;
    paper: string;
    custom: string;
    body: string;
  };
  text: {
    primary: string;
    secondary: string;
  };
  link: string;
  highlightedRow: string;
  borders: {
    appBar: string;
  };
  dataGrid: {
    header: string;
  };
};

/* ===========================
   COMPONENT OVERRIDES (TEK)
=========================== */
const getSharedComponents = (mode: "light" | "dark", c: ThemeColors) => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundColor: c.background.body,
        color: c.text.primary,
      },
      "main.MuiBox-root": {
        backgroundColor: c.background.custom,
      },
      a: {
        color: c.link,
        textDecoration: "none",
        fontWeight: 500,
        "&:hover": {
          textDecoration: "underline",
        },
      },
    },
  },

  MuiAppBar: {
    defaultProps: {
      color: "default" as const,
      elevation: 0,
    },
    styleOverrides: {
      root: {
        backgroundColor: c.background.paper,
        height: "64px",
        color: c.text.primary,
        borderBottom: c.borders.appBar,
      },
    },
  },

  MuiButton: {
    styleOverrides: {
      root: ({
        ownerState,
      }: {
        ownerState: { variant?: string; color?: string };
      }) => ({
        ...(ownerState.variant === "contained" &&
          ownerState.color === "primary" && {
            boxShadow: "none",
          }),
      }),
    },
  },

  MuiTypography: {
    defaultProps: {
      variant: "body2" as const,
    },
  },

  MuiDataGrid: {
    styleOverrides: {
      root: {
        minHeight: 500,
        backgroundColor: c.background.paper,
        borderRadius: 6,
      },
      columnHeader: {
        backgroundColor: c.dataGrid.header,
        fontWeight: 600,
      },
      footerContainer: {
        backgroundColor: c.dataGrid.header,
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        fontWeight: 500,
      },
    },
  },
});

/* ===========================
   THEME FACTORY (TEK)
=========================== */
export const createAppTheme = (mode: "light" | "dark", colors: ThemeColors) =>
  responsiveFontSizes(
    createTheme({
      palette: {
        mode,
        primary: {
          ...colors.primary,
          contrastText: "#ffffff",
        },
        secondary: colors.secondary,
        background: {
          default: colors.background.default,
          paper: colors.background.paper,
        },
        customBackground: {
          box: colors.background.custom,
        },
        highlightedRow: {
          main: colors.highlightedRow,
        },
        text: colors.text,
        link: {
          main: colors.link,
        },
      },
      components: getSharedComponents(mode, colors),
    }),
  );
